// Phase 10A.1 — Shared truthful empty-state primitive.
//
// Doctrinal posture: every visible STRIKZA product surface must be
// real, empty, or clearly educational — never fake. Routes that
// don't yet have real data flowing through them get this panel
// instead of mock fixtures, mock balances, mock chat histories, or
// mock recommendations.
//
// Token-only styling. No hex literals, no rgb/rgba, no new tokens.
// Reuses --bg-elev-1 / --border-faint / --text-primary /
// --text-secondary / --font-* families already in the new shell.

import type { ReactNode } from "react";

export interface AwaitingDataPanelProps {
  readonly title: string;
  readonly body: ReactNode;
  /**
   * Optional eyebrow that appears above the title (uppercase mono
   * by convention). Useful when the panel sits inside a route that
   * also shows generic page chrome above it.
   */
  readonly eyebrow?: string;
}

export function AwaitingDataPanel({ title, body, eyebrow }: AwaitingDataPanelProps): JSX.Element {
  return (
    <div
      style={{
        marginTop: 24,
        padding: 32,
        border: "1px dashed var(--border-faint, #2a2a2a)",
        borderRadius: 8,
        background: "var(--bg-elev-1, transparent)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {eyebrow !== undefined ? (
        <span
          style={{
            fontFamily: "var(--font-mono, monospace)",
            fontSize: 11,
            letterSpacing: "2px",
            textTransform: "uppercase",
            color: "var(--text-tertiary, #666)",
            lineHeight: 1.4,
          }}
        >
          {eyebrow}
        </span>
      ) : null}
      <div
        style={{
          fontFamily: "var(--font-head, inherit)",
          fontSize: 18,
          color: "var(--text-primary, inherit)",
          lineHeight: 1.3,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: "var(--font-body, inherit)",
          fontSize: 14,
          color: "var(--text-secondary, inherit)",
          lineHeight: 1.55,
        }}
      >
        {body}
      </div>
    </div>
  );
}
